import { formatMobileNumber } from "./util";

export type CustomerSearchDisplay = {
  name: string;
  ownerName: string;
  associationName: string;
  mobileNumber: string;
  /** Display form of RMN, e.g. "98765 43210" */
  formattedMobileNumber: string;
  initials: string;
  avatarBg: string;
  avatarColor: string;
};

const AVATAR_PALETTE = [
  { bg: "#DBEAFE", color: "#1D4ED8" }, // blue
  { bg: "#DCFCE7", color: "#15803D" }, // green
  { bg: "#FFEDD5", color: "#C2410C" }, // orange
  { bg: "#F3E8FF", color: "#7E22CE" }, // purple
  { bg: "#FCE7F3", color: "#BE185D" }, // pink
] as const;

/**
 * The association label is the parent supplier's name when the associated
 * supplier is a branch (child) supplier, falling back to the supplier's own
 * name. Both are populated by the customer search API.
 */
function getAssociationName(customer: any): string {
  const association = customer?.supplierAssociations?.[0];
  const supplier = association?.parentSupplier ?? association?.supplier;
  return supplier?.businessName || supplier?.name || "";
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getAvatarColors(seed: string): { bg: string; color: string } {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/** Formats a 10-digit national number as "98765 43210". */
export function formatMobileForSearchDisplay(mobileNumber: string): string {
  const digits = mobileNumber.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return mobileNumber;
}

export function getCustomerSearchDisplay(
  customer: any,
): CustomerSearchDisplay {
  const name = `${customer?.name ?? ""}`.trim();
  const mobileNumber = formatMobileNumber(customer?.mobileNumber);
  const avatar = getAvatarColors(name || mobileNumber || "customer");

  return {
    name,
    ownerName: `${customer?.ownerName ?? ""}`.trim(),
    associationName: getAssociationName(customer),
    mobileNumber,
    formattedMobileNumber: formatMobileForSearchDisplay(mobileNumber),
    initials: getInitials(name),
    avatarBg: avatar.bg,
    avatarColor: avatar.color,
  };
}
